<template>
  <top-navbar
    v-if="
      showLayout &&
        !isIframe &&
        route.name !== 'NotFoundMag' &&
        route.name !== 'NotFoundMagGeneral'
    "
  />
  <main
    class="error-page-container"
    :class="[
      route.name === 'NotFoundMag' ? 'background-mag' : '',
      isIframe ? 'widget-container' : ''
    ]"
    :style="isIframe ? getWidgetContainerStyle : {}"
  >
    <div class="message-container" :style="isIframe ? getWidgetTextStyle : {}">
      <i
        ><img
          :src="require('@/assets/images/ic-error-notice.svg')"
          :alt="$t('wegow.error.altErrorIcon')"
      /></i>
      <p :style="isIframe ? getWidgetTextStyle : {}">{{ customMessage || $t("wegow.error.pageNotFound") }}</p>
      <w-navigation
        :params="
          route.name !== 'NotFoundMag' && route.name !== 'NotFoundMagGeneral'
            ? { name: 'home' }
            : { name: 'MagazineMainPage' }
        "
        :full="false"
      >
        <template #link>
          <wegow-button
            id="go-to-wegow-button"
            background="opacity"
            color="topaz"
            type="button"
            :text="$t('general.routes.goHome')"
            :style="isIframe ? getWidgetButtonStyle : {}"
            v-if="!isIframe"
          />
        </template>
      </w-navigation>
    </div>
  </main>
  <div v-if="showLayout && !isIframe">
    <basic-footer v-if="route.name !== 'NotFoundMag'" />
  </div>
</template>

<script setup>
import { onBeforeMount, onMounted, computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import BasicFooter from "@/components/general/ui/footer/BasicFooter";
import TopNavbar from "@/components/general/ui/navbar/TopNavbar";
import WegowButton from "@/components/general/forms/WegowButton";
import WNavigation from "@/components/general/ui/WNavigation";
import { metas } from "@/plugins/mixins/metas";
import { useEvent } from "@/composables/useEvent";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { isIframe, iframeStyles, getDetails } = useEvent();
const widgetStylesLoaded = ref(false);

const props = defineProps({
  showLayout: {
    type: Boolean,
    default: true
  },
  customMessage: {
    type: String,
    default: null
  }
});

const extractSlugFromWidgetUrl = () => {
  const path = route.path;
  
  if (path.includes('/widget/') || path.includes('/waiting-room/')) {
    const widgetMatch = path.match(/\/widget\/([^\/]+)/);
    const waitingRoomMatch = path.match(/\/waiting-room\/([^\/]+)/);
    
    if (widgetMatch && widgetMatch[1]) {
      return widgetMatch[1];
    } else if (waitingRoomMatch && waitingRoomMatch[1]) {
      return waitingRoomMatch[1];
    }
  }
  
  return null;
};

const loadWidgetStyles = async () => {
  const slug = extractSlugFromWidgetUrl();
  
  if (slug) {
    try {
      await getDetails(slug);
      widgetStylesLoaded.value = true;
    } catch (error) {
      console.error('Error loading widget styles:', error);
      widgetStylesLoaded.value = false;
    }
  }
};

const getWidgetContainerStyle = computed(() => {
  if (!isIframe.value || !iframeStyles.value) return {};
  
  return {
    backgroundColor: iframeStyles.value.background_color || '#000214',
    backgroundImage: 'none',
    paddingTop: '0',
    minHeight: '100vh',
    marginBottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
});

const getWidgetTextStyle = computed(() => {
  if (!isIframe.value || !iframeStyles.value) return {};
  
  return {
    color: iframeStyles.value.font_color || iframeStyles.value.title_color || '#FFFFFF'
  };
});

const getWidgetButtonStyle = computed(() => {
  if (!isIframe.value || !iframeStyles.value) return {};
  
  const style = {};
  
  if (iframeStyles.value.button_color) {
    style.backgroundColor = iframeStyles.value.button_color;
  }
  
  if (iframeStyles.value.font_color || iframeStyles.value.aux_font_color) {
    style.color = iframeStyles.value.aux_font_color || iframeStyles.value.font_color;
  }
  
  if (iframeStyles.value.button_color && (iframeStyles.value.font_color || iframeStyles.value.aux_font_color)) {
    style.borderColor = iframeStyles.value.button_color;
  }
  
  return style;
});

onMounted(async () => {
  await loadWidgetStyles();
  
  metas.methods.setLanguage();
  metas.methods.setCanonical();
  // Establecer el estado de respuesta HTTP como 404
  const statusCode = 404;
  const statusText = "Not Found";
  history.replaceState({ statusCode, statusText }, "", window.location.href);

  // Agregar la etiqueta "noindex" al head del documento
  const metaTag = document.createElement("meta");
  metaTag.name = "robots";
  metaTag.content = "noindex";
  document.head.appendChild(metaTag);
});
onBeforeMount(() => {
  metas.methods.setMetasHtml(
    {
      title: t("general.metas.notFoundTitle"),
      description: t("general.metas.notFoundDescription")
    },
    route,
    router
  );
});
</script>
<style lang="scss" scoped>
@media screen and (max-width: 700px) {
  #id-container {
    padding: 14px !important;
  }
  .message-container {
    padding-left: 20px;
    padding-right: 20px;
  }
  #go-to-wegow-button {
    margin-top: 50px !important;
  }
}
.error-page-container {
  width: 100%;
  min-height: 100vh;
  background-image: url("@/assets/images/background-error-page.png");
  background-repeat: round;
  padding-top: 93px;
}
.error-page-container p {
  font-size: calc(16px + (40 - 16) * ((100vw - 300px) / (1600 - 300)));
}
.message-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 788px;
  margin-right: auto;
  margin-left: auto;
  margin-bottom: 0;
  text-align: center;
}

.widget-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

.widget-container .message-container {
  margin-bottom: 0;
  margin-top: 0;
  padding: 20px;
}

.widget-container #go-to-wegow-button {
  margin-top: 40px;
}
.message-container i {
  margin-bottom: 27px;
}
#go-to-wegow-button {
  margin-top: 40px;
}
.background-mag {
  background-color: $backgroundBlack;
  color: $white;
}
</style>
